import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Wallet, FileDown, Upload } from "lucide-react";
import { toast } from "sonner";
import { onErr, downloadFile, inr, num, todayStr } from "@/lib/providers";

export default function ProviderPayouts() {
  const qc = useQueryClient();
  const { data: rows, isLoading, isError, refetch } = useQuery({ queryKey: ["spPayouts"], queryFn: async () => (await api.get("/sp/payouts")).data });
  const [invFor, setInvFor] = useState(null);
  const [inv, setInv] = useState({ number: "", date: todayStr(), file: null });

  const upload = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("invoice_number", inv.number); fd.append("invoice_date", inv.date); fd.append("file", inv.file);
      return (await api.post(`/sp/payouts/${invFor.id}/invoice`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: () => { toast.success("Invoice submitted"); qc.invalidateQueries({ queryKey: ["spPayouts"] }); setInvFor(null); },
    onError: onErr,
  });

  if (isLoading) return <Loading />;
  if (isError) return <ErrorState onRetry={refetch} />;

  return (
    <div>
      <PageHeader overline="Service Provider" title="Payout Advices" icon={Wallet}
        subtitle="IntenCiv prepares an advice for your verified work. Upload your invoice for it, and payment follows approval." />
      {!rows?.length ? <EmptyState icon={Wallet} title="No payout advices yet" hint="Advices appear once your work logs are verified." /> : (
        <div className="space-y-3">
          {rows.map((r) => (
            <Card key={r.id} className="p-4 border-slate-200 shadow-none">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-mono text-xs text-teal">{r.advice_number} · {r.wo_number}</p>
                  <p className="font-semibold text-navy">{new Date(r.month + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</p>
                </div>
                <StatusBadge status={r.status} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3 text-sm">
                <div><p className="text-xs text-slate-400">Verified units</p>{num(r.billable_units)}</div>
                <div><p className="text-xs text-slate-400">Service value</p>{inr(r.taxable_value)}</div>
                <div><p className="text-xs text-slate-400">GST</p>{r.gst_amount ? inr(r.gst_amount) : "—"}</div>
                <div><p className="text-xs text-slate-400">TDS ({r.tds_section})</p>{inr(r.tds_amount)}</div>
                <div><p className="text-xs text-slate-400">Net payable</p><span className="font-semibold text-navy">{inr(r.net_payable)}</span></div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {r.invoice_number ? `Your invoice ${r.invoice_number} dated ${r.invoice_date}` : `Please raise your invoice for ${inr(r.invoice_total)}.`}
                {r.status === "Paid" && ` · Paid on ${r.paid_on} (ref ${r.payment_reference})`}
              </p>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={() => downloadFile(`/sp/payouts/${r.id}/pdf`, `PayoutAdvice_${r.advice_number}.pdf`)}><FileDown className="h-4 w-4 mr-1" /> Advice PDF</Button>
                {r.status === "Draft" && (
                  <Button size="sm" className="bg-navy hover:bg-navy-700" onClick={() => { setInvFor(r); setInv({ number: r.invoice_number || "", date: todayStr(), file: null }); }}>
                    <Upload className="h-4 w-4 mr-1" /> {r.invoice_number ? "Replace invoice" : "Upload my invoice"}
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!invFor} onOpenChange={(o) => !o && setInvFor(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Upload invoice — {inr(invFor?.invoice_total)}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Invoice no. *</Label><Input className="mt-1" value={inv.number} onChange={(e) => setInv({ ...inv, number: e.target.value })} /></div>
            <div><Label>Invoice date *</Label><Input type="date" className="mt-1" value={inv.date} onChange={(e) => setInv({ ...inv, date: e.target.value })} /></div>
          </div>
          <div><Label>Invoice file (PDF or photo) *</Label><Input type="file" accept=".pdf,.jpg,.jpeg,.png" className="mt-1" onChange={(e) => setInv({ ...inv, file: e.target.files?.[0] || null })} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvFor(null)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!inv.number || !inv.date || !inv.file || upload.isPending} onClick={() => upload.mutate()}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
